import React, { useEffect, useState } from "react";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  updateProfile,
} from "firebase/auth";
import { auth } from "../firebase/firebase";
import { loadUserProfile, saveUserProfile, getUserCounts } from "../services/data";

/**
 * Profil utilisateur (lecture/ecriture Firestore)
 * - Doc: users/{uid}/settings/profile
 * - Champs branches: displayName, bio, locale
 */
export default function ProfilePage({ user, onBack }) {
  const [form, setForm] = useState({
    displayName: user?.displayName || "",
    bio: "",
    locale: "fr",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [counts, setCounts] = useState({ docs: 0, modeles: 0 });
  const [pwdEditing, setPwdEditing] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const canSavePwd =
    !pwdSaving &&
    pwdEditing &&
    pwdCurrent.trim().length >= 6 &&
    pwdNew.trim().length >= 6 &&
    pwdNew === pwdConfirm;

  const computedName = form.displayName || user?.displayName || "Utilisateur";
  const email = user?.email || "-";
  const initials = (computedName || email || "U").trim()[0].toUpperCase();

  useEffect(() => {
    let off = false;
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const cur = await loadUserProfile(user.uid);
        if (off) return;
        setForm({
          displayName: cur?.displayName ?? user?.displayName ?? "",
          bio: cur?.bio || "",
          locale: cur?.locale || "fr",
        });
      } finally {
        if (!off) setLoading(false);
      }
    })();
    return () => {
      off = true;
    };
  }, [user?.uid, user?.displayName]);

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      try {
        const c = await getUserCounts(user.uid);
        setCounts(c);
      } catch (e) {
        console.warn("getUserCounts:", e?.message || e);
      }
    })();
  }, [user?.uid]);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSave = async () => {
    if (!user?.uid) return;
    setSaving(true);
    try {
      await saveUserProfile(user.uid, {
        displayName: form.displayName || "",
        bio: form.bio || "",
        locale: form.locale || "fr",
      });
      try {
        if (
          auth.currentUser &&
          form.displayName &&
          auth.currentUser.displayName !== form.displayName
        ) {
          await updateProfile(auth.currentUser, {
            displayName: form.displayName,
          });
        }
      } catch (e) {
        console.warn("updateProfile:", e?.message || e);
      }
      alert("Profil enregistré.");
      setEditing(false);
      if (typeof onBack === "function") onBack();
    } catch (e) {
      console.error(e);
      alert("Echec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const hasPasswordProvider = () => {
    const u = auth.currentUser;
    return !!u?.providerData?.some((p) => p?.providerId === "password");
  };

  const startPwdEdit = () => {
    if (!user?.email) {
      alert("Aucun email associé à ce compte.");
      return;
    }
    if (!hasPasswordProvider()) {
      alert(
        "Ce compte semble connecté via un fournisseur externe (ex. Google). La définition d’un mot de passe local n’est pas disponible ici."
      );
      return;
    }
    setPwdEditing(true);
    setPwdCurrent("");
    setPwdNew("");
    setPwdConfirm("");
  };

  const cancelPwdEdit = () => {
    setPwdEditing(false);
    setPwdCurrent("");
    setPwdNew("");
    setPwdConfirm("");
  };

  const savePassword = async () => {
    if (!canSavePwd) return;
    if (!auth.currentUser || !user?.email) return;

    setPwdSaving(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, pwdCurrent);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, pwdNew);
      alert("Mot de passe modifié avec succès.");
      cancelPwdEdit();
      if (typeof onBack === "function") onBack();
    } catch (e) {
      console.error(e);
      const msg = String(e?.code || e?.message || e);
      if (msg.includes("wrong-password")) {
        alert("Mot de passe actuel incorrect.");
      } else if (msg.includes("weak-password")) {
        alert("Nouveau mot de passe trop faible (≥ 6 caractères).");
      } else if (msg.includes("requires-recent-login")) {
        alert("Action sensible : reconnectez-vous puis réessayez.");
      } else {
        alert("Échec de la modification du mot de passe : " + msg);
      }
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <main className="main-content">
      <div className="page-header">
        <h1 className="page-title">
          <i className="fas fa-user" /> Mon profil
        </h1>
        <div className="quick-actions">
          {onBack && (
            <button className="btn btn-outline" onClick={onBack}>
              <i className="fas fa-arrow-left" /> Retour
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving || loading || !editing}
          >
            <i className="fas fa-save" /> Enregistrer
          </button>
        </div>
      </div>

      <div className="profile-container">
        {/* Colonne gauche */}
        <aside className="profile-sidebar">
          <div className="profile-avatar">{initials}</div>
          <div className="profile-name">{computedName}</div>
          <div className="profile-email">{email}</div>
          <div className="profile-role">Utilisateur</div>
          <div className="profile-stats">
            <div className="stat-item">
              <div className="stat-value">{counts.docs}</div>
              <div className="stat-label">Docs</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{counts.modeles}</div>
              <div className="stat-label">Modeles</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">-</div>
              <div className="stat-label">Equipes</div>
            </div>
          </div>
          <div className="profile-actions">
            <button
              className="btn btn-outline"
              onClick={() => setEditing((v) => !v)}
              disabled={loading}
            >
              <i className="fas fa-pen" /> {editing ? "Annuler" : "Éditer"}
            </button>
          </div>
        </aside>

        {/* Colonne droite */}
        <section className="profile-content">
          {/* Infos perso */}
          <div className="profile-section">
            <div className="section-header">
              <div className="section-title">
                <i className="fas fa-id-card" /> Informations personnelles
              </div>
            </div>
            <div className="section-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Nom complet</label>
                  <input
                    className="form-input"
                    value={form.displayName}
                    onChange={(e) => upd("displayName", e.target.value)}
                    placeholder="Votre nom"
                    disabled={loading || !editing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" value={email} disabled />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Bio</label>
                  <textarea
                    className="form-textarea"
                    value={form.bio}
                    onChange={(e) => upd("bio", e.target.value)}
                    placeholder="Decrivez-vous en quelques mots..."
                    disabled={loading || !editing}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sécurité */}
          <div className="profile-section">
            <div className="section-header">
              <div className="section-title">
                <i className="fas fa-shield-alt" /> Sécurité
              </div>
            </div>
            <div className="section-body">
              <div className="security-item">
                <div className="security-info">
                  <div className="security-title">Mot de passe</div>
                  <div className="security-desc">
                    {pwdEditing
                      ? "Saisissez votre mot de passe actuel et le nouveau."
                      : "Protégez votre compte avec un mot de passe solide."}
                  </div>
                </div>
                <div className="security-status">
                  <span className="status-badge status-active">Actif</span>
                </div>
                <div className="security-action">
                  {!pwdEditing ? (
                    <button className="btn btn-outline" onClick={startPwdEdit}>
                      <i className="fas fa-key" /> Modifier
                    </button>
                  ) : (
                    <div style={{ display: "grid", gap: 8, minWidth: 280 }}>
                      <input
                        className="form-input"
                        type="password"
                        placeholder="Mot de passe actuel"
                        value={pwdCurrent}
                        onChange={(e) => setPwdCurrent(e.target.value)}
                        disabled={pwdSaving}
                      />
                      <input
                        className="form-input"
                        type="password"
                        placeholder="Nouveau mot de passe"
                        value={pwdNew}
                        onChange={(e) => setPwdNew(e.target.value)}
                        disabled={pwdSaving}
                      />
                      <input
                        className="form-input"
                        type="password"
                        placeholder="Confirmer le nouveau mot de passe"
                        value={pwdConfirm}
                        onChange={(e) => setPwdConfirm(e.target.value)}
                        disabled={pwdSaving}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="btn btn-outline"
                          onClick={cancelPwdEdit}
                          disabled={pwdSaving}
                        >
                          Annuler
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={savePassword}
                          disabled={!canSavePwd}
                        >
                          {pwdSaving ? "En cours..." : "Enregistrer"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="profile-section">
            <div className="section-header">
              <div className="section-title">
                <i className="fas fa-sliders-h" /> Preferences
              </div>
            </div>
            <div className="section-body">
              <div className="preferences-list">
                <div className="preference-item">
                  <div className="preference-info">
                    <div className="preference-icon">
                      <i className="fas fa-language" />
                    </div>
                    <div className="preference-title">Langue</div>
                  </div>
                  <div className="preference-desc">
                    Choisissez la langue de l'interface
                  </div>
                  <select
                    className="form-select"
                    value={form.locale}
                    onChange={(e) => upd("locale", e.target.value)}
                    disabled={loading || !editing}
                  >
                    <option value="fr">Francais</option>
                    <option value="en">English</option>
                    <option value="ar">Arabe</option>
                    <option value="ru">Russe</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
